import { YBlurEvent, YEventHandlers, YFocusEvent, YInputEvent, YStateEvent, YValueEvent } from '../../y/events';
import { YInput } from '../../y/form';
import { yFillN } from '../../y/util';
import { uiAnimate } from '../animate';
import { onAttach } from '../attach';
import { $el } from '../elements';
import { onKey, UIKey } from '../key';
import { UIClassesSpec } from '../types';
import { UIView } from '../view';

interface UICodeInputOptions {
    length?: 4 | 5 | 6;
    classes?: UIClassesSpec;
    required?: boolean;
    disabled?: boolean;
    autofocus?: boolean;
}

export class UICodeInput extends UIView implements YInput<string | undefined> {
    $handlers: YEventHandlers<YStateEvent & YInputEvent & YValueEvent<string | undefined> & YBlurEvent & YFocusEvent>;

    private _value: string;
    private _disabled!: boolean;
    private _required: boolean;
    private _locked: boolean;

    private _input: HTMLInputElement;
    private _digits: HTMLDivElement[];
    private _focused: boolean;
    private _final_regex: RegExp;

    okay!: boolean;
    invalid?: boolean;
    dirty: boolean;

    on_code?: (code: string) => boolean | Promise<boolean>;

    constructor(options: UICodeInputOptions = {}) {
        super('div', ['ui-code', 'i-input', options.classes]);

        const length = options.length || 6;

        this.dirty = false;
        this._required = !!options.required;
        this._value = '';
        this._focused = false;
        this._locked = false;

        const input = this._input = $el.INPUT('text', 'ui-code-input');
        input.autocomplete = 'one-time-code';
        input.inputMode = 'numeric';
        input.name = 'one-time-code';

        const digits = this._digits = yFillN(length, () => $el.DIV('ui-code-digit'));

        this.ui_add([input, digits]);

        this.disabled = !!options.disabled;

        this._fixDesign();

        const regex = new RegExp(`^[0-9]{0,${length}}$`);
        this._final_regex = new RegExp(`^[0-9]{${length}}$`);

        input.addEventListener('input', () => {
            const value = input.value;

            if(this._locked || this._value.length >= length || !regex.test(value)) {
                input.value = '';
                return;
            }

            if(value.length == 1) {
                this._digits[this._value.length].textContent = value;
                this._value += value;
                input.value = '';
                this._fixDesign();
                if(this._value.length == length)
                    this._doCode(this._value);
            } else if(value.length == length) {
                this.value = value;
            }
        });

        onKey(input, e => {
            if(this._locked) return;

            if(e.key === UIKey.Backspace) {
                if(this._value.length) {
                    this._value = this._value.slice(0, -1);
                    this._digits[this._value.length].textContent = '';
                    this._fixDesign();
                    return true;
                }
            }
            return;
        });


        input.addEventListener('focus', () => {
            if(this._locked) {
                input.blur();
                return;
            }
            this._focused = true;
            this._fixDesign();
        });

        input.addEventListener('blur', () => {
            this._focused = false;
            this._fixDesign();
        });

        this.root.addEventListener('click', () => {
            if(!this._focused)
                this.focus();
        });

        if(options.autofocus) {
            onAttach(this, () => {
                this.focus();
            });
        }
    }

    private _fixDesign() {
        const focused = this._focused;
        const next_digit = this._value.length;
        const input = this._input;
        const digits = this._digits;
        for(let i = 0; i < digits.length; i++) {
            const digit = digits[i];
            digit.classList.toggle('f-focus', focused && i == next_digit);
        }
        input.classList.toggle('u-gone', next_digit == digits.length);
        if(next_digit < digits.length) {
            input.style.left = `${digits[next_digit].offsetLeft}px`;
            input.style.width = `${digits[next_digit].offsetWidth}px`;
        }
    }

    protected setOkay(o: boolean) {
        if(o === this.okay) return;

        // if(o && this._invalid_mark) {
        //     this.classList.remove('f-invalid');
        //     this._invalid_mark = false;
        // }

        // this.okay = o;
        // $trigger(this, 'state');
    }

    protected setDirty(_d: boolean) {
        // if(this._invalid_mark) {
        //     this.classList.remove('f-invalid');
        //     this._invalid_mark = false;
        // }

        // if(d === this.dirty) return;

        // this.dirty = d;
        // $trigger(this, 'state');
    }

    protected doSetDisabled(_d: boolean) {
    }

    private async _doCode(code: string) {
        if(!this.on_code) return;

        let ok = this.on_code(code);

        if(typeof ok != 'boolean') {
            this.blur();
            this._locked = true;
            ok = await ok;
            this._locked = false;
        }

        if(!ok) {
            for(const d of this._digits) {
                uiAnimate([{ what: d, animation: 'flash-invalid-code', duration: 1.5 }]);
            }
            this.value = '';
            this.focus();
        }
    }

    focus() {
        this._input.focus();
    }

    blur() {
        this._input.blur();
    }

    get disabled() {
        return this._disabled;
    }

    set disabled(d: boolean) {
        this._disabled = d;
        this.classList.toggle('f-disabled', d);
        this.doSetDisabled(d)
    }

    get required() {
        return this._required;
    }

    set required(r: boolean) {
        this._required = r;
        // this.doCheck();
    }

    get value(): string | undefined {
        if(this._value.length == this._digits.length)
            return this._value;
        return undefined;
    }

    set value(v: string | undefined) {
        if(!v) {
            this._value = '';
            for(let i = 0; i < this._digits.length; i++) {
                this._digits[i].textContent = '';
            }
            this._fixDesign();
        } else if(this._final_regex.test(v)) {
            this._value = v;
            for(let i = 0; i < this._digits.length; i++) {
                this._digits[i].textContent = v[i];
            }
            this._fixDesign();
            this._doCode(v);
        }
    }
}
